import type {
  AthleteWithAll,
  CompatibleRemoteAgeCategory,
  CompatibleRemoteDiscipline,
  CompatibleRemoteWeightCategory,
  DisplayMode,
  GroupWithAll,
  LevelRemoteWithAll,
  MeetWithAll,
  RecordRemoteWithAll,
  ThemeColors,
  RefereeType,
} from "../types.js";
import type {
  BehaviorConnection,
  FontFamilyValue,
  BehaviorNode as BlueprintGraphNode,
  FontWeightValue,
  TextAlignValue,
  TextDecorationStyleValue,
  TextTransformValue,
  WhiteSpaceValue,
  VariableValueType,
} from "../blueprint/types.js";

import type { Prisma as PrismaRemote } from "@prisma-remote/client";
/**
 * Core type definitions for the Display Editor layout system.
 *
 * A LayoutDocument describes a visual layout as a tree of nodes (primitives,
 * containers, components) with data bindings and conditional styles.
 * The schema lives in packages/util so it can be shared between the editor
 * (packages/ui), the renderer and both apps (Electron & Web).
 */

// ---------------------------------------------------------------------------
// Document root
// ---------------------------------------------------------------------------

export interface LayoutDocument {
  version: number;
  meta: LayoutMeta;
  scene: LayoutScene;
}

export interface LayoutMeta {
  id: string;
  name: string;
  type: "widget" | "display";
  canvas: { width: number; height: number };
  /**
   * Layout discipline scope. "default" keeps the legacy behavior, while
   * "standard:{id}" and "custom:{id}" target a discipline-specific layout.
   */
  disciplineKey?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LayoutScene {
  children: LayoutNode[];
  backgroundColor?: string;
  /** Scene-level catalog of reusable animations, events, functions and variables. */
  catalog?: SceneBehaviorCatalog;
  /**
   * Last allocated auto-name index per node type key
   * (e.g. `{ "primitive:text": 3 }`). Used to assign stable `node.nameIndex`
   * values; display falls back to `Type {nameIndex}` when `label` is empty.
   */
  nodeNameCounters?: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Scene Behavior Catalog
// ---------------------------------------------------------------------------

export interface BehaviorGraph {
  nodes: BlueprintGraphNode[];
  connections: BehaviorConnection[];
}

export interface SceneBehaviorCatalog {
  animations: SceneAnimationEntry[];
  customEvents: SceneCustomEventEntry[];
  functions: SceneFunctionEntry[];
  variables: SceneVariableEntry[];
  /** Optional for layouts saved before reusable object types were introduced. */
  objectTypes?: SceneObjectTypeEntry[];
}

export interface SceneObjectField {
  key: string;
  valueType: "string" | "number" | "boolean" | "date" | "object" | "list";
  objectTypeId?: string;
  defaultValue?: unknown;
}

/** A reusable shape: base type → immutable extension → named output type. */
export interface SceneObjectTypeEntry {
  id: string;
  name: string;
  /** `source:currentAthlete`, another object-type ID, or null for an empty object. */
  baseType: string | null;
  fields: SceneObjectField[];
  /** Additional spread inputs; baseType is the first, backwards-compatible extension. */
  extensions?: { id: string; typeId: string }[];
  /** Optional for types created with the former form editor. */
  graph?: BehaviorGraph;
}

export interface SceneAnimationEntry {
  id: string;
  name: string;
  graph: BehaviorGraph;
}

export interface SceneCustomEventEntry {
  id: string;
  name: string;
  /** Trigger composition graph - events + mergeFlows → eventOutput. */
  triggerGraph: BehaviorGraph;
}

export interface SceneFunctionEntry {
  id: string;
  name: string;
  description?: string;
  inputs: SceneFunctionPort[];
  outputs: SceneFunctionPort[];
  graph: BehaviorGraph;
}

/** Port of a scene function - may include "flow" and value types. */
export interface SceneFunctionPort {
  id: string;
  label: string;
  valueType: string;
}

/**
 * Input port of a scene variable - value only, never "flow".
 * Dedicated type so "flow" is excluded at compile-time and JSON validation.
 */
export interface SceneVariableInputPort {
  id: string;
  label: string;
  valueType: VariableValueType;
  objectTypeId?: string;
}

export interface SceneVariableEntry {
  id: string;
  name: string;
  outputValueType: VariableValueType;
  outputObjectTypeId?: string;
  inputs: SceneVariableInputPort[];
  graph: BehaviorGraph;
}

export const EMPTY_SCENE_CATALOG: SceneBehaviorCatalog = {
  animations: [],
  customEvents: [],
  functions: [],
  variables: [],
  objectTypes: [],
};

// ---------------------------------------------------------------------------
// Nodes
// ---------------------------------------------------------------------------

export type LayoutNode = PrimitiveNode | ContainerNode | ComponentNode;

interface BaseNode {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Rotation in degrees applied to the node wrapper. */
  rotation?: number;
  zIndex: number;
  locked?: boolean;
  /**
   * Optional user-facing custom name. When empty, the editor displays
   * `"{Type} {nameIndex}"` instead.
   */
  label?: string;
  /**
   * Persistent auto-name index for this node (per type). Decoupled from
   * `label`: clearing the label falls back to `Type {nameIndex}`.
   */
  nameIndex?: number;
  style?: LayoutNodeStyle;
  transitions?: TransitionConfig[];
  /** Optional behavior graph attached to this node. */
  behavior?: {
    nodes: BlueprintGraphNode[];
    connections: BehaviorConnection[];
  };
  dataBinding?: DataBinding;
  valueKind?: "primitive" | "binding";
}

/**
 * Tooltip shown on hover for a primitive node. Only editable/visible in the
 * custom-discipline Display Editor modes (`customDisciplineCell` /
 * `customDisciplineModal`); invisible and without effect in `standard` mode.
 */
export type TextTooltipDefinition =
  | {
      kind: "text";
      text: string;
    }
  | {
      kind: "layout";
      layout: LayoutDocument;
      backgroundColor?: string;
      opacity?: number;
    };

export interface PrimitiveNode extends BaseNode {
  kind: "primitive";
  primitive: "text" | "image" | "video" | "shape";
  /** Optional hover tooltip (custom-discipline modes only). */
  tooltip?: TextTooltipDefinition | null;
  /**
   * Text primitive only: when `fitContent`, the renderer uses CSS `width: fit-content`
   * so the box shrinks to the text; node.width stays as fallback/minimum for tooling.
   */
  textWidthMode?: "fixed" | "fitContent";
  text?: string;
  /** Media source URL (for primitive === "image" or "video"). */
  src?: string;
  /** Mute state for primitive === "video". */
  muted?: boolean;
  /** Loop state for primitive === "video". */
  loop?: boolean;
  /** Shape type (for primitive === "shape"). */
  shapeType?:
    | "rectangle"
    | "square"
    | "ellipse"
    | "oval"
    | "line"
    | "triangle"
    | "rightTriangle"
    | "trapezoid"
    | "parallelogram"
    | "pentagon"
    | "hexagon"
    | "octagon"
    | "star";
}

export interface ContainerNode extends BaseNode {
  kind: "container";
  layout: "flex" | "absolute";
  flexWidthMode?: "fixed" | "fitContent";
  flexHeightMode?: "fixed" | "fitContent";
  flexDirection?: "row" | "row-reverse" | "column" | "column-reverse";
  justifyContent?:
    | "center"
    | "flex-start"
    | "flex-end"
    | "normal"
    | "space-between"
    | "space-around"
    | "space-evenly";
  alignItems?:
    | "center"
    | "flex-start"
    | "flex-end"
    | "space-between"
    | "space-around"
    | "space-evenly";
  gap?: number;
  children: LayoutNode[];
}

export interface UserLayoutThemeInfo {
  id: string;
  name: string;
  userId: string | null;
  isPublic: boolean;
}

export const DEFAULT_USER_LAYOUT_DISCIPLINE_KEY = "default";
export const STANDARD_USER_LAYOUT_DISCIPLINE_KEY_PREFIX = "standard:";
export const CUSTOM_USER_LAYOUT_DISCIPLINE_KEY_PREFIX = "custom:";

export function getStandardUserLayoutDisciplineKey(
  disciplineId: string | number,
): string {
  return `${STANDARD_USER_LAYOUT_DISCIPLINE_KEY_PREFIX}${disciplineId}`;
}

export function getCustomUserLayoutDisciplineKey(
  disciplineId: string | number,
): string {
  return `${CUSTOM_USER_LAYOUT_DISCIPLINE_KEY_PREFIX}${disciplineId}`;
}

export function normalizeUserLayoutDisciplineKey(value: unknown): string {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : DEFAULT_USER_LAYOUT_DISCIPLINE_KEY;
}

export interface UserLayoutListItem {
  id: string;
  name: string;
  type: string;
  canvasWidth: number;
  canvasHeight: number;
  backgroundColor: string | null;
  scene?: LayoutScene;
  version: number;
  themeId: string | null;
  targetKey: string | null;
  disciplineKey: string;
  theme?: UserLayoutThemeInfo | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Valid target keys for display-type layouts.
 */
export const DISPLAY_TARGET_KEYS = [
  "athlete_display",
  "loader_display",
  "ranking_display",
  "public_display",
  "lights_display",
] as const;

/**
 * Valid target keys for widget-type layouts.
 */
export const WIDGET_TARGET_KEYS = [
  "current_athlete",
  "current_category",
  "current_group",
  "referee_lights",
  "timer",
] as const;

export type DisplayTargetKey = (typeof DISPLAY_TARGET_KEYS)[number];
export type WidgetTargetKey = (typeof WIDGET_TARGET_KEYS)[number];
export type TargetKey = DisplayTargetKey | WidgetTargetKey;

/**
 * Maps an Electron display window mode to the `targetKey` stored on remote layouts.
 *
 * Referee lights windows use `referee_lights` as `DisplayMode`, while display-type
 * layouts in the editor are tagged with `lights_display`.
 *
 * @param mode - Electron display settings / window mode.
 * @returns The layout target key used in themes, or `null` for `main`.
 */
export function displayModeToLayoutTargetKey(
  mode: DisplayMode,
): DisplayTargetKey | null {
  if (mode === "main") {
    return null;
  }
  if (mode === "referee_lights") {
    return "lights_display";
  }
  return mode;
}

export interface ComponentNode extends BaseNode {
  kind: "component";
  component: ComponentKind;
  props?: ComponentPropsByKind[ComponentKind];
}

export type PrimitiveKind = "text" | "image" | "video" | "shape";
export type LayoutListMode = "preview" | "edit";

/** Flow direction for stacked rows in preview mode. */
export type LayoutListDirection =
  | "column"
  | "row"
  | "column-reverse"
  | "row-reverse";

/**
 * CSS properties allowed in `layoutTransition.properties`.
 * Kept as a const tuple so both runtime validation and the property panel
 * multi-select can share the same source of truth.
 */
export const TRANSITION_PROPERTIES = [
  "transform",
  "left",
  "top",
  "height",
  "width",
  "opacity",
  "background-color",
  "color",
  "border",
  "border-radius",
  "box-shadow",
  "font-size",
  "padding",
  "margin",
] as const;

export type TransitionProperty = (typeof TRANSITION_PROPERTIES)[number];

export interface TransitionConfig {
  enabled?: boolean;
  property: TransitionProperty;
  durationMs?: number;
  easing?: string;
  delayMs?: number;
}

export interface LayoutListComponentProps {
  /** Display mode. Outside the editor, renderer should always force preview. */
  mode?: LayoutListMode;
  /** Variant currently selected in edit mode. Undefined means the default row template. */
  editRowVariantName?: string;
  /** Maximum number of list items rendered in preview mode. */
  maxItems?: number;
  /**
   * Dot-path to a unique identifier field on each list item (e.g. "id", "licence").
   * Required for stable diff-based enter/exit detection and CSS relayout transitions.
   */
  rowKeyPath?: string;
  /** Stack direction for preview rows. Defaults to `"column"`. */
  direction?: LayoutListDirection;
  /** Repeated row template for each item in the bound list. */
  rowTemplate: ContainerNode;
  rowVariants: {
    name: string;
    template: ContainerNode;
  }[];
}

export interface CustomDisciplineInputComponentProps {
  /** Optional empty-list presentation for reference selectors. */
  emptyOptionsText?: string;
  disableWhenEmpty?: boolean;
  labelShrink?: boolean;
  size?: "small" | "medium";
  key?: string;
  label?: string;
  type?: "TEXT" | "NUMBER" | "SELECT" | "BOOLEAN" | "DATE" | "FOREIGN_KEY";
  defaultValue?: string | number | boolean | null;
  required?: boolean;
  readOnly?: boolean;
  fieldKey?: string | null;
  optionsMode?: "static" | "behavior";
  staticOptions?: { value: string; label: string }[];
  optionSource?: string | null;
  optionValuePath?: string | null;
  optionLabelPath?: string | null;
}

export interface CustomDisciplineButtonComponentProps {
  autoFocus?: boolean;
  label?: string;
  startIcon?: string | null;
  variant?: "text" | "outlined" | "contained";
  size?: "small" | "medium" | "large";
  color?:
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning";
  disabled?: boolean;
}

export interface IconComponentProps {
  iconName?: string | null;
  color?: string | null;
  size?: number;
}

export type ComponentKind =
  | "barLoader"
  | "layoutList"
  | "qrCode"
  | "icon"
  | "customDisciplineInput"
  | "customDisciplineButton";

export interface ComponentPropsByKind {
  barLoader: Record<string, unknown>;
  layoutList: LayoutListComponentProps;
  qrCode: Record<string, unknown>;
  icon: IconComponentProps;
  customDisciplineInput: CustomDisciplineInputComponentProps;
  customDisciplineButton: CustomDisciplineButtonComponentProps;
}

// ---------------------------------------------------------------------------
// Data sources
// ---------------------------------------------------------------------------

export type DataSourceId = keyof InjectedData;

// ---------------------------------------------------------------------------
// Bindings
// ---------------------------------------------------------------------------

export interface DataBinding {
  path: string;
}

export type PrimitiveValue =
  | string
  | number
  | boolean
  | undefined
  | Date
  | null;

export function isPrimitiveValue(value: unknown): value is PrimitiveValue {
  return (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    value instanceof Date
  );
}

export function getDataBindingSource(
  binding?: DataBinding,
): DataSourceId | undefined {
  if (!binding || !binding.path.split(".")[0]) {
    return undefined;
  }
  return binding.path.split(".")[0] as DataSourceId;
}

// ---------------------------------------------------------------------------
// Style with conditions
// ---------------------------------------------------------------------------

export type LayoutNodeStyleKey =
  | "visibility"
  | "position"
  | "overflow"
  | "breakBefore"
  | "breakInside"
  | "width"
  | "minWidth"
  | "height"
  | "margin"
  | "marginTop"
  | "marginRight"
  | "marginBottom"
  | "marginLeft"
  | "backgroundColor"
  | "backgroundImage"
  | "color"
  | "fontSize"
  | "lineHeight"
  | "letterSpacing"
  | "fontFamily"
  | "fontWeight"
  | "textAlign"
  | "textTransform"
  | "whiteSpace"
  | "textDecorationLine"
  | "textDecorationColor"
  | "textDecorationStyle"
  | "textDecorationThickness"
  | "border"
  | "borderTop"
  | "borderRight"
  | "borderBottom"
  | "borderLeft"
  | "borderRadius"
  | "borderTopLeftRadius"
  | "borderTopRightRadius"
  | "borderBottomRightRadius"
  | "borderBottomLeftRadius"
  | "opacity"
  | "padding"
  | "paddingTop"
  | "paddingRight"
  | "paddingBottom"
  | "paddingLeft"
  | "transform"
  | "filter"
  | "boxShadow"
  | "textShadow"
  | "cursor"
  | "transition";

export type LayoutNodeStyleValueType =
  | Extract<
      VariableValueType,
      | "boolean"
      | "number"
      | "string"
      | "color"
      | "gradient"
      | "border"
      | "fontFamily"
      | "fontWeight"
      | "textAlign"
      | "textTransform"
      | "whiteSpace"
      | "textDecorationLine"
      | "textDecorationColor"
      | "textDecorationStyle"
      | "textDecorationThickness"
      | "filter"
    >
  | "spacing";

export interface LayoutNodeStyleValueByKey {
  visibility: boolean;
  position: "relative" | "absolute";
  overflow: "visible" | "hidden" | "auto" | "scroll";
  breakBefore: "auto" | "page" | "avoid";
  breakInside: "auto" | "avoid";
  width: number | "auto";
  minWidth: number | string;
  height: number | "auto";
  margin: number | string;
  marginTop: number | string;
  marginRight: number | string;
  marginBottom: number | string;
  marginLeft: number | string;
  backgroundColor: string;
  backgroundImage: string;
  color: string;
  fontSize: number;
  lineHeight: number | string;
  letterSpacing: number | string;
  fontFamily: FontFamilyValue;
  fontWeight: FontWeightValue;
  textAlign: TextAlignValue;
  textTransform: TextTransformValue;
  whiteSpace: WhiteSpaceValue;
  textDecorationLine: string;
  textDecorationColor: string;
  textDecorationStyle: TextDecorationStyleValue;
  textDecorationThickness: number;
  border: string;
  borderTop: string;
  borderRight: string;
  borderBottom: string;
  borderLeft: string;
  borderRadius: number | string;
  borderTopLeftRadius: string;
  borderTopRightRadius: string;
  borderBottomRightRadius: string;
  borderBottomLeftRadius: string;
  opacity: number;
  padding: number | string;
  paddingTop: number | string;
  paddingRight: number | string;
  paddingBottom: number | string;
  paddingLeft: number | string;
  transform: string;
  filter: string;
  boxShadow: string;
  textShadow: string;
  cursor: string;
  transition: string;
}

export type LayoutNodeStyleValue =
  LayoutNodeStyleValueByKey[LayoutNodeStyleKey];

export type LayoutNodeStyle = Partial<{
  [K in LayoutNodeStyleKey]: LayoutNodeStyleValueByKey[K];
}>;

export interface LayoutNodeStyleFieldDefinition {
  key: LayoutNodeStyleKey;
  valueType: LayoutNodeStyleValueType;
  category: LayoutNodeStyleCategory;
  appliesTo: readonly LayoutStyleFieldTarget[];
}

export type LayoutNodeStyleCategory =
  | "layout"
  | "font"
  | "decoration"
  | "border";
export type LayoutStyleFieldTarget =
  | "primitive:text"
  | "primitive:image"
  | "primitive:video"
  | "primitive:shape"
  | "container"
  | "component"
  | "component:qrCode"
  | "component:customDisciplineInput"
  | "component:customDisciplineButton";

const LAYOUT_STYLE_ALL_TARGETS: readonly LayoutStyleFieldTarget[] = [
  "primitive:text",
  "primitive:image",
  "primitive:video",
  "primitive:shape",
  "container",
  "component",
  "component:qrCode",
  "component:customDisciplineInput",
  "component:customDisciplineButton",
] as const;

const LAYOUT_STYLE_TEXT_TARGETS: readonly LayoutStyleFieldTarget[] = [
  "primitive:text",
  "component:customDisciplineInput",
  "component:customDisciplineButton",
] as const;

/** QR code: `color` drives module color (`fgColor`); other font keys are hidden. */
const LAYOUT_STYLE_QR_CODE_TEXT_TARGETS: readonly LayoutStyleFieldTarget[] = [
  "component:qrCode",
] as const;

export const LAYOUT_NODE_STYLE_FIELDS: readonly LayoutNodeStyleFieldDefinition[] =
  [
    {
      key: "visibility",
      valueType: "boolean",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "overflow",
      valueType: "string",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "breakBefore",
      valueType: "string",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "breakInside",
      valueType: "string",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "backgroundColor",
      valueType: "color",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "backgroundImage",
      valueType: "gradient",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "opacity",
      valueType: "number",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "margin",
      valueType: "spacing",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "transform",
      valueType: "string",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "filter",
      valueType: "filter",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "cursor",
      valueType: "string",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "boxShadow",
      valueType: "string",
      category: "layout",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "fontSize",
      valueType: "number",
      category: "font",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "fontFamily",
      valueType: "fontFamily",
      category: "font",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "fontWeight",
      valueType: "fontWeight",
      category: "font",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "color",
      valueType: "color",
      category: "font",
      appliesTo: [
        ...LAYOUT_STYLE_TEXT_TARGETS,
        ...LAYOUT_STYLE_QR_CODE_TEXT_TARGETS,
      ],
    },
    {
      key: "textAlign",
      valueType: "textAlign",
      category: "font",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "textTransform",
      valueType: "textTransform",
      category: "font",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "whiteSpace",
      valueType: "whiteSpace",
      category: "font",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "textShadow",
      valueType: "string",
      category: "font",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "textDecorationLine",
      valueType: "textDecorationLine",
      category: "decoration",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "textDecorationColor",
      valueType: "color",
      category: "decoration",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "textDecorationStyle",
      valueType: "textDecorationStyle",
      category: "decoration",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "textDecorationThickness",
      valueType: "number",
      category: "decoration",
      appliesTo: LAYOUT_STYLE_TEXT_TARGETS,
    },
    {
      key: "border",
      valueType: "border",
      category: "border",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
    {
      key: "borderRadius",
      valueType: "border",
      category: "border",
      appliesTo: LAYOUT_STYLE_ALL_TARGETS,
    },
  ] as const;

export function getLayoutNodeStyleTarget(
  node: LayoutNode,
): LayoutStyleFieldTarget {
  if (node.kind === "primitive") {
    return `primitive:${node.primitive}`;
  }
  if (node.kind === "container") {
    return "container";
  }
  if (node.kind === "component" && node.component === "qrCode") {
    return "component:qrCode";
  }
  if (
    node.kind === "component" &&
    (node.component === "customDisciplineInput" ||
      node.component === "customDisciplineButton")
  )
    return `component:${node.component}`;
  return "component";
}

export function getLayoutNodeStyleFields(
  node: LayoutNode,
): readonly LayoutNodeStyleFieldDefinition[] {
  const target = getLayoutNodeStyleTarget(node);
  return LAYOUT_NODE_STYLE_FIELDS.filter((field) =>
    field.appliesTo.includes(target),
  );
}

export type AnimationEasing =
  | "linear"
  | "ease"
  | "ease-in"
  | "ease-out"
  | "ease-in-out";

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

/** Simple condition: left op right (or unary defined/empty). Legacy JSON without type is treated as simple. */
export interface SimpleCondition {
  type?: "simple";
  left: ConditionOperand;
  op: ConditionOperator;
  right?: ConditionOperand;
}

/** AND: all children must evaluate to true. */
export interface AndCondition {
  type: "and";
  conditions: ConditionExpr[];
}

/** OR: at least one child must evaluate to true. */
export interface OrCondition {
  type: "or";
  conditions: ConditionExpr[];
}

export type ConditionExpr = SimpleCondition | AndCondition | OrCondition;

export type ConditionOperator =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "defined"
  | "empty";

export type ConditionOperand = ConditionLiteral | ConditionBinding;

export interface ConditionLiteral {
  type: "literal";
  value: unknown;
}

export interface ConditionBinding {
  type: "binding";
  source: DataSourceId;
  path?: string;
}

// ---------------------------------------------------------------------------
// Injected data (provided at runtime by each app's DataProvider)
// ---------------------------------------------------------------------------
export interface AthleteData extends AthleteWithAll {}

export interface MeetData
  extends Omit<
    MeetWithAll,
    "groups" | "records" | "niveaux" | "localWidgetsURLs" | "machineId"
  > {}

export interface GroupData extends Omit<GroupWithAll, "athletes"> {}

export interface RecordData extends RecordRemoteWithAll {}

export interface LevelData extends LevelRemoteWithAll {}

export interface LeagueData extends PrismaRemote.LigueGetPayload<{}> {}

/**
 * Competition organiser identity exposed to custom layouts.
 * Scalar Organisation fields only (no federationConfig / children relations).
 */
export interface OrganisationData {
  id: string;
  name: string;
  code: string | null;
  countryCode: string | null;
  recordLabel: string | null;
  recordColor: string | null;
  recordBadgeUrl: string | null;
  parentId: string | null;
}

export interface RefereeLights {
  type: RefereeType;
  passed: boolean;
  color: string;
  juryEnabled: boolean;
}

export interface NextLiftData {
  athlete: AthleteData;
  movement: "squat" | "bench" | "deadlift";
  attemptNumber: number;
}

export interface InjectedData {
  /** Shared Electron-owned timer readings for the current competition. */
  behaviorTimers?: import("../behavior-timers/protocol.js").BehaviorTimerData;
  behaviorTimerScope?: string;
  /** Administrator labels, for presentation only (never persisted with athlete data). */
  __customDisciplineLabels?: Record<string, string>;
  /** Raw cell value for custom-discipline cell layouts (`currentValue` binding). */
  currentValue?: string | number | boolean | null;
  /** Custom-discipline row record keyed by field `key` (cell/modal layouts). */
  athlete?: Record<string, unknown> | null;
  currentAthlete?: AthleteData | null;
  athletes?: AthleteData[];
  /** Grid selection context for custom-discipline cell layouts. */
  selectedRowId?: string;
  selectedColumnKey?: string;
  /**
   * Custom-discipline context group (opened group page or evaluated meet row).
   * Distinct from {@link InjectedData.currentGroup} (meet-selected focus).
   */
  group?: GroupData | null;
  currentGroup?: GroupData | null;
  groups?: GroupData[];
  currentMeet?: MeetData | null;
  timer?: string;
  timerSeconds?: number;
  attemptTimers?: string[];
  attemptTimerSeconds?: number[];
  currentMovement?: "squat" | "bench" | "deadlift" | null;
  attemptNumber?: number;
  placementIfSuccessful?: number | null;
  refereeLights?: RefereeLights[] | null;
  nextLiftData?: NextLiftData | null;
  records?: RecordData[];
  levels?: LevelData[];
  disciplineAgeCategories?: import("../custom-disciplines/category-catalog.js").DisciplineAgeRule[];
  disciplineWeightCategories?: import("../custom-disciplines/category-catalog.js").DisciplineWeightRule[];
  ageCategories?: CompatibleRemoteAgeCategory[];
  weightCategories?: CompatibleRemoteWeightCategory[];
  disciplines?: CompatibleRemoteDiscipline[];
  leagues?: LeagueData[];
  /** Organisation of the current competition (meet.organisationId). */
  organisation?: OrganisationData | null;
  /** Public organisation identities and hierarchy, cached with the custom catalogs. */
  organisations?: OrganisationData[];
  themeColors?: ThemeColors | null;
  /** Host display preferences; sport rules remain in the document's Behavior graphs. */
  displaySettings?: {
    lightsEnabled: boolean;
    automaticRefereeing?: boolean;
    showLights: boolean;
    webClientUrl: string;
  };

  /**
   * Context values
   */
  listTransitionSignal?:
    | "onListEnter"
    | "onListExit"
    | "onListFilterExit"
    | "onListFilterEnter";
  currentNode?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    rotation?: number;
    skewX?: number;
    skewY?: number;
  } | null;
  rowIndex?: number;
  layoutListRowItem?: unknown;
  /** Indices compared by `layoutList` `setSort` (original list positions). */
  sortAIndex?: number;
  sortBIndex?: number;
  /**
   * Per-item iteration scope used by generic list operators
   * (`findByCondition`, `filter`, …). Read by the `currentItem` operator.
   * Intentionally distinct from `layoutListRowItem` / `rowIndex`
   * (owned by `LayoutList`) so the two mechanisms never shadow each other.
   */
  iterationItem?: unknown;
  iterationIndex?: number;
  /**
   * Per-pair iteration scope used by the generic `sort` list operator.
   * Read by the `comparator` operator (outputs `outputA` / `outputB`).
   * Distinct from `iterationItem` so a `sort` comparator can still nest
   * a `findByCondition` / `filter` without colliding scopes.
   */
  iterationItemA?: unknown;
  iterationItemB?: unknown;
  /**
   * Values of the row currently open in a custom discipline modal
   * (e.g. `customOpenModal` payload). Read by `customDiscipline` sources.
   */
  modalValues?: Record<string, unknown>;
  /**
   * Live values of `customDisciplineInput` fields in the current modal,
   * keyed by `fieldKey`. Written back by the host page on user input.
   */
  inputValues?: Record<string, unknown>;
}
